import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import TrendArea from '../../../dashboard/TrendArea';
import { DeltaChip } from '../../../dashboard/primitives';
import { SalesTrend } from '../../../types/salesAnalytics';
import { formatDateShortIN } from '../../../pages/sales/salesReportsHelpers';

const WINDOW_OPTIONS = [30, 90, 180, 365];

interface RevenueTrendCardProps {
  trend: SalesTrend | null;
  loading: boolean;
  days: number;
  onDaysChange: (days: number) => void;
}

/**
 * Revenue trend from GET /sales-analytics/trend?metric=revenue.
 *
 * Reuses TrendArea as-is per the design kit rather than a bespoke chart —
 * `connectNulls={false}` inside it already renders a snapshot-less day as a
 * gap, never a straight line drawn through data that doesn't exist.
 */
const RevenueTrendCard: React.FC<RevenueTrendCardProps> = ({ trend, loading, days, onDaysChange }) => {
  const points = (trend?.points ?? []).map((p) => ({ label: formatDateShortIN(p.date), value: p.value }));

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Revenue Trend</Typography>
          <Typography variant="caption" color="text.secondary">
            Daily invoiced revenue, last {trend?.days ?? days} days
            {trend?.coverage ? ` · ${trend.coverage} days recorded` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {trend && <DeltaChip value={trend.changePct} />}
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            {WINDOW_OPTIONS.map((w) => (
              <Chip
                key={w}
                label={`${w}d`}
                size="small"
                clickable
                color={days === w ? 'primary' : 'default'}
                variant={days === w ? 'filled' : 'outlined'}
                onClick={() => onDaysChange(w)}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: 260 }} />
      ) : (
        <TrendArea points={points} category="sales" currency height={260} />
      )}
    </Paper>
  );
};

export default RevenueTrendCard;
